export const CONTACT_EMAIL = "heartopia.guide@proton.me";
export const DONATE_URL = "https://lucashdo.com/donate?project=Heartopia%20Guide";
export const SITE_URL = "https://heartopia.guide";
export const SITE_NAME = "Heartopia.guide";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/web-app-manifest-512x512.png`;

export const SITE_TITLE_SUFFIX = ` | ${SITE_NAME}`;

/**
 * Roughly where Bing and Google stop showing a `<title>`. Not a hard rule — both truncate by
 * pixel width, not characters — so this is a budget, not a guarantee.
 */
export const TITLE_MAX_CHARS = 65;

/**
 * Appends the site name only while it still fits.
 *
 * Past the budget the suffix is pure waste: it is the part that gets cut, so it costs 18
 * characters of a truncated title to display branding nobody sees. Dropping it lets more of the
 * actual page name survive. Titles longer than the budget on their own keep losing their tail —
 * that is a copywriting problem, not something this can fix — but they at least stop spending
 * their last characters on the suffix.
 *
 * **Both renderers must agree.** useHeadTags sets document.title on the client and
 * scripts/generate-static-meta.ts writes it into the prerendered HTML; if only one applied this
 * rule, the title would visibly change the moment React hydrated, and a JS-executing crawler would
 * index the other one.
 */
export function withSiteName(base: string): string {
  const full = `${base}${SITE_TITLE_SUFFIX}`;
  return full.length <= TITLE_MAX_CHARS ? full : base;
}

/** The same rule for a title that already carries the suffix — what the prerender deals with,
 * since its titles are assembled (and in STATIC_PAGE_COPY, hardcoded) with it baked in. */
export function fitTitle(title: string): string {
  if (title.length <= TITLE_MAX_CHARS) return title;
  return title.endsWith(SITE_TITLE_SUFFIX) ? title.slice(0, -SITE_TITLE_SUFFIX.length) : title;
}

/** Removes the suffix so a title from the shared copy table can go back through withSiteName,
 * which expects a bare base. Doing it in one step would double the suffix on short titles. */
export function stripSiteSuffix(title: string): string {
  return title.endsWith(SITE_TITLE_SUFFIX) ? title.slice(0, -SITE_TITLE_SUFFIX.length) : title;
}
